import { journalSourcePolicy } from './journal-feature-policy';

// Feature flags. Vite reads VITE_*_* values from .env files at build
// time and exposes them via import.meta.env. Flags stay off in
// production until their owning chunk ships its terminal step. Update
// the production env when a feature graduates from flag-gated to
// always-on; never delete the flag inline without ratifying the rip.
export const PICKER_ENABLED = import.meta.env.VITE_PICKER_ENABLED === 'true';

// Bible reader Study Mode (tap-to-define glossary). Lookupable words
// glow gold when Study Mode is on; tap opens a definition sheet.
// Source data lives in scripts/glossary/{glossary,locations}-master.json
// and is built into public/glossary/headwords.json, manifest.json, and
// occurrences/<slug>.json by scripts/glossary/build-public-data.mjs.
// Default off until the curated coverage feels production-ready; flag-on
// loads ~80-100 KB gzipped of glossary data lazily on first toggle, so
// flag-off pays zero bundle cost.
export const GLOSSARY_ENABLED =
  import.meta.env.VITE_GLOSSARY_ENABLED === 'true';

// Bible reader Pray Mode. A "Pray" pill (paired with Study as an
// underline-text toggle) lets the user select verses or a whole chapter
// and pray on them; the saved prayer carries a scriptureAnchor and flows
// through the normal garden pipeline. Default off until the chunk's
// terminal step ships.
export const PRAY_MODE_ENABLED =
  import.meta.env.VITE_PRAY_MODE_ENABLED === 'true';

// Bible reader Bookmarks + notes. A "Bookmark" pill (third toggle beside
// Study and Pray) lets the user select one or more verses and save them as
// a bookmark with an optional spoken/typed note (whole-chapter bookmarking
// was cut in the thread redesign). A SAVED verse is a tappable thread: it
// opens all notes saved on it over time, each with an immutable stamp, plus
// add/edit/delete. Bookmarks are local-first + synced. This flag gates the
// header toggle, the reader thread markers, and the compose/thread sheet;
// the /bible/bookmarks list page + index button are remaining Phase 4 work.
// LIVE in prod (repo default off). See the thread redesign + feature plans
// under docs/plans/bible-bookmarks-*.
export const BIBLE_BOOKMARKS_ENABLED =
  import.meta.env.VITE_BIBLE_BOOKMARKS_ENABLED === 'true';

// Curated-verse-match (the verse lane): the prayer's struggle is
// classified into the operator's 12 categories and every curated verse
// above the fit bar renders, Jesus-words entries first
// (/api/grok-verse-suggest over public/bible-search/struggle-verses.json).
// Searching happens in the share flow's "Find Scripture for this prayer"
// fork AND the Garden vine card's "Find" verse action (Chunk 2); the
// read-only surfaces (PrayerDetail's "Scripture for this prayer" line,
// the Garden card's "View") only RE-READ a saved result. en-only at v1
// (same carve-out class); held-close prayers never reach the SEARCH
// (rule #27, caller is the gate) but DO show their saved verses in the
// read-only views. Default off. Plan:
// docs/plans/curated-verse-match-plan-2026-06-10.md +
// docs/plans/cut-tap-to-match-keep-verses-2026-06-12.md +
// docs/plans/garden-verse-action-chunk2-plan-2026-06-17.md.
export const VERSE_SUGGEST_ENABLED =
  import.meta.env.VITE_VERSE_SUGGEST_ENABLED === 'true';

// Bible reader rethink Chunk B: the verse-tap interaction layer. Tapping the
// verse RUN selects it and a slim action bar (Keep / Pray / Share) appears;
// tapping more verses extends the selection. Keep is the one verb (a kept verse
// is one bookmark row that can hold a note); tapping an already-KEPT verse opens
// its thread (the rich view). Retires the mutually-exclusive Pray / Bookmark
// MODES under v2 (no Pray/Bookmark header toggles, hint banners, or bottom
// bars); Study/glossary glow is KEPT verbatim and coexists (glow word = define,
// elsewhere = select). The v1 mode INTERACTION stays byte-for-byte under flag-off
// (dual-path); the reader THEME/background is shared (decoupled from this flag),
// so the desktop full-bleed reader-bg fix applies on flag-off builds too. Chunk G
// strips the dead v1 UI post-cutover. Default off until the
// arc previews. Plan: docs/plans/bible-reader/
// bible-reader-living-bible-rethink-chunkB-verse-tap-redesign-spec.md.
export const BIBLE_READER_V2_ENABLED =
  import.meta.env.VITE_BIBLE_READER_V2_ENABLED === 'true';

/** Route/entry availability only; individual source reads remain independently
 * gated by journalSourcePolicy in useJournalSources. */
export const SCRIPTURE_JOURNAL_ENABLED = journalSourcePolicy(
  {
    bookmarks: BIBLE_BOOKMARKS_ENABLED,
    highlights: BIBLE_READER_V2_ENABLED,
    prayers: PRAY_MODE_ENABLED,
  },
  true,
).journalEnabled;

// Bible reader route-level off-switch (prayer-only pivot, 2026-06-12).
// Gates the /bible* routes in App.tsx plus the two entry points (PrayPage
// book icon, Settings "Open the Bible" row). Default OFF: the prayer-only
// app ships with the in-app Bible reader dark while the reader + its data
// pipelines are removed in later reviewed chunks. The share card's browse
// verse picker is independent of this flag (it reads the Supabase taxonomy,
// not the reader). Plan: docs/plans/prayer-only-pivot-2026-06-12.md.
export const BIBLE_ENABLED =
  import.meta.env.VITE_BIBLE_ENABLED === 'true';

// Bible context overview (the "map of Scripture", 2026-09-12). A searchable,
// optional catalog of the operator-authored book + chapter orientation notes
// (scripture_book_contexts + scripture_chapter_contexts) at /bible/overview,
// read from the static bundle public/bible-search/bible-contexts.json so it
// supports offline reads within existing authentication gates. Gates the routes and entry
// row on the /bible index. Purely additive: the in-chapter BibleContextBlock
// disclosure (rule #70) is untouched. Default OFF; the operator sets
// VITE_BIBLE_OVERVIEW_ENABLED=true in Vercel once the bundle is committed.
export const BIBLE_OVERVIEW_ENABLED =
  import.meta.env.VITE_BIBLE_OVERVIEW_ENABLED === 'true';

// App-wide auth gate (hard gate). When on, an unauthenticated visitor sees a
// full-screen sign-in gate (the tree-by-stream HomePrayerScene look) and cannot
// reach any route until they authenticate. Default OFF in the repo; the operator
// sets VITE_AUTH_GATE_ENABLED=true in Vercel to turn the hard gate on (same
// pattern as BIBLE_ENABLED). Flag OFF skips this eager presentation gate, but
// the downstream owner gate still requires authentication whenever a Supabase
// backend is configured; it may fall open only for a backend-free, core-read-
// only build where no cloud owner exists and all mutations remain blocked.
// Switchable to a true SOFT gate later only with an explicit owner-safe product
// design. Plan: docs/plans/auth-gate/auth-gate-2026-06-25.md.
export const AUTH_GATE_ENABLED =
  import.meta.env.VITE_AUTH_GATE_ENABLED === 'true';
